import { useEffect } from 'react';
import styled from 'styled-components';
import {
  Alert,
  Flex,
  FlexColumn,
  Link,
  LoadingButton,
} from '@elementary/common-ui/components';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { CircularProgress, Typography } from '@mui/material';
import useFivetranIntegration from 'shared/hooks/integrations/useFivetranIntegration';
import SensitiveInfoDisplay from '../../../shared/SensitiveInfoDisplay';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';

const InlineCode = styled.code`
  font-family: 'Geist Mono', monospace;
  font-size: inherit;
  background: ${({ theme }) => theme.palette.common.gray6};
  border-radius: 4px;
  padding: 1px 4px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  envId: string;
  fivetranIntegrationId?: string;
};

const FivetranDialog = ({
  open,
  onClose,
  envId,
  fivetranIntegrationId,
}: Props) => {
  const mode = fivetranIntegrationId ? 'view' : 'create';
  const { createMutation, revealMutation } = useFivetranIntegration({ envId });

  useEffect(() => {
    if (!open) {
      revealMutation.reset();
      createMutation.reset();
      return;
    }
    if (mode === 'view' && fivetranIntegrationId) {
      revealMutation.mutate(fivetranIntegrationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const webhookUrlPath =
    createMutation.data?.webhook_url_path ??
    revealMutation.data?.webhook_url_path;
  const webhookUrl = webhookUrlPath
    ? `${import.meta.env.VITE_API_BASE_URL}${webhookUrlPath}`
    : undefined;
  const sharedSecret =
    createMutation.data?.shared_secret ?? revealMutation.data?.shared_secret;

  const credentialsReady = !!(webhookUrl && sharedSecret);

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect Fivetran"
      description="Elementary connects to Fivetran via webhook to automatically create incidents when connector syncs fail."
      hideSaveButton
    >
      <FlexColumn sx={{ gap: 3 }}>
        {!credentialsReady && !revealMutation.isLoading && (
          <>
            <FlexColumn>
              <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                To set up the connection:
              </Typography>
              <Typography component="ol" sx={{ pl: 3 }}>
                <li>Generate webhook credentials below</li>
                <li>
                  In Fivetran, find the Destination Group ID under the Overview
                  tab of the destination to monitor
                </li>
                <li>
                  Register a <InlineCode>sync_end</InlineCode> webhook for that
                  group using the{' '}
                  <Link
                    href="https://fivetran.com/docs/rest-api/api-reference/webhooks/create-group-webhook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Fivetran Webhooks API
                  </Link>{' '}
                  with the generated credentials
                </li>
              </Typography>
            </FlexColumn>
            {mode === 'create' && !createMutation.data && (
              <>
                <LoadingButton
                  onClick={() => createMutation.mutate(undefined)}
                  loading={createMutation.isLoading}
                  loadingPosition="center"
                >
                  Generate Webhook Credentials
                </LoadingButton>
                {createMutation.isError && (
                  <Alert severity="error">
                    Failed to enable Fivetran integration. Please try again.
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        {revealMutation.isError && (
          <Alert severity="error">
            Failed to load credentials. Please try again.
          </Alert>
        )}

        {revealMutation.isLoading && (
          <Flex sx={{ justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Flex>
        )}

        {credentialsReady && (
          <>
            <FlexColumn>
              <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                To complete the setup:
              </Typography>
              <Typography component="ol" sx={{ pl: 3 }}>
                <li>
                  In Fivetran, find the Destination Group ID under the Overview
                  tab of the destination to monitor
                </li>
                <li>
                  Register a <InlineCode>sync_end</InlineCode> webhook for that
                  group using the{' '}
                  <Link
                    href="https://fivetran.com/docs/rest-api/api-reference/webhooks/create-group-webhook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Fivetran Webhooks API
                  </Link>{' '}
                  with the following credentials:
                </li>
              </Typography>
            </FlexColumn>
            <SensitiveInfoDisplay label="Webhook URL" value={webhookUrl} />
            <SensitiveInfoDisplay label="Secret" value={sharedSecret} />
            {revealMutation.data?.last_event_received_at && (
              <Alert severity="success" title="Fivetran Connected">
                Last event received:{' '}
                {localizeDate(
                  revealMutation.data.last_event_received_at,
                ).format('L LT')}
              </Alert>
            )}
          </>
        )}
      </FlexColumn>
    </IntegrationDialog>
  );
};

export default FivetranDialog;
